/**
 * The "when did the user last do something" half of the idle timeout.
 *
 * Kept apart from `idleClock.ts` so the timing rules stay pure: this module is
 * the only one that touches storage, and it is still plain TypeScript with no
 * React, so it runs under the node-environment vitest config.
 *
 * Two design decisions here are load-bearing and neither is obvious.
 *
 * 1. **Activity means DOM events, never network traffic.** The criteria require
 *    that background calls — the notifications WebSocket, the polling hooks, the
 *    token renewal in `authFetch` — do not keep a walked-away session alive.
 *    Instrumenting requests to tell foreground from background would mean
 *    touching 53 files whose failure mode is exactly the thing being forbidden.
 *    Every foreground request is caused by an interaction this module already
 *    sees, so listening to the DOM is both simpler and stricter.
 *
 * 2. **The timestamp lives in localStorage, not a module variable.** That is
 *    what makes several tabs on one machine share one clock: a write in any tab
 *    is visible to all of them, and the `storage` event tells the others
 *    immediately.
 */

const LAST_ACTIVITY_KEY = 'cgt.auth.lastActivityAt'

/**
 * Why a session ended, read once by the login screen.
 *
 * It has to survive a full page load: `handleUnauthorized` navigates with
 * `window.location.assign`, which discards React state and any router
 * `location.state`, so there is nowhere else to put it.
 */
const ENDED_REASON_KEY = 'cgt.auth.endedReason'

export type SessionEndedReason = 'inactivity'

/**
 * Smallest gap between two writes.
 *
 * `pointermove` is not among the tracked events, but `scroll` still fires in
 * bursts, and writing to localStorage on every one would serialise a string per
 * frame. Five seconds is far below any usable timeout, so the loss of precision
 * cannot change an outcome.
 */
const WRITE_THROTTLE_MS = 5000

/**
 * The events that count as the user being present.
 *
 * `visibilitychange` and `focus` are handled separately by the caller, because
 * returning to a tab should re-evaluate the clock but must NOT reset it — a
 * machine that slept past the threshold has to expire on return rather than be
 * granted a fresh window.
 */
export const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'scroll'] as const

/**
 * Negative infinity, not 0: `Date.now()` is ~1.7e12, so a zero sentinel would
 * make the first write look like it happened 1.7e12ms ago only by luck — and in
 * a test, or any clock near the epoch, the FIRST write would be swallowed by
 * the throttle and the clock would never start.
 */
let lastWriteAt = Number.NEGATIVE_INFINITY

/** Storage can throw in a private window; a dead clock must not crash the app. */
function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Quota or a blocked store. The clock degrades to per-tab memory rather
    // than taking the session down with it.
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // See safeWrite.
  }
}

export function getLastActivityAt(): number | null {
  const raw = safeRead(LAST_ACTIVITY_KEY)
  if (!raw) return null
  const parsed = Number(raw)
  // A corrupt value must not read as 1970, which would expire every session on
  // load. Treat it as "unknown" and let the next interaction reset it.
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Record that the user did something.
 *
 * `force` bypasses the throttle for the writes that must land exactly: starting
 * the clock on sign-in, and the explicit "Stay signed in" press, which has to
 * reset a clock that by definition has not seen activity for a while.
 */
export function markActivity(now: number = Date.now(), force = false): void {
  if (!force && now - lastWriteAt < WRITE_THROTTLE_MS) return
  lastWriteAt = now
  safeWrite(LAST_ACTIVITY_KEY, String(now))
}

/**
 * Start (or restart) the clock, always writing.
 *
 * For an actual sign-in only. Calling it on every app load was a bug: it
 * overwrote the stored timestamp with "now" each time the page opened, so
 * closing the browser and coming back later always granted a fresh window and
 * the timeout only ever applied to a tab left open. Page loads use
 * `resumeIdleClock` instead.
 */
export function startIdleClock(now: number = Date.now()): void {
  markActivity(now, true)
}

/**
 * On opening the app while already signed in: keep the clock that is running,
 * and start one only if none exists.
 *
 * Keeping it is the whole point. The stored timestamp is what lets a long
 * absence expire the session — whether the tab slept, was closed, or the
 * browser was quit — so the first evaluation after load can end it rather than
 * granting a fresh window.
 *
 * Starting one when absent covers sessions that predate the clock, and a store
 * that was cleared. Without it such a session would never expire: an absent
 * timestamp evaluates as "not started", and nothing would start it until the
 * user next interacted.
 *
 * Returns whether it started a new clock, for the test.
 */
export function resumeIdleClock(now: number = Date.now()): boolean {
  if (getLastActivityAt() !== null) return false
  startIdleClock(now)
  return true
}

export function clearIdleClock(): void {
  lastWriteAt = Number.NEGATIVE_INFINITY
  safeRemove(LAST_ACTIVITY_KEY)
}

/** Test seam: the throttle is module state and would otherwise leak between cases. */
export function resetActivityThrottle(): void {
  lastWriteAt = Number.NEGATIVE_INFINITY
}

export function isActivityStorageKey(key: string | null): boolean {
  return key === LAST_ACTIVITY_KEY
}

export function setSessionEndedReason(reason: SessionEndedReason): void {
  safeWrite(ENDED_REASON_KEY, reason)
}

/**
 * Read the reason and clear it in one go.
 *
 * Consuming on read is the point: the message explains the sign-out that just
 * happened, so leaving it behind would caption every later visit to the login
 * screen with a timeout that is no longer true.
 */
export function takeSessionEndedReason(): SessionEndedReason | null {
  const raw = safeRead(ENDED_REASON_KEY)
  if (!raw) return null
  safeRemove(ENDED_REASON_KEY)
  return raw === 'inactivity' ? 'inactivity' : null
}
